/**
 * Work mode (personal / work / programming) filters which categories and tasks
 * are visible. The choice is persisted on the user row so it follows you
 * between devices, with localStorage as an instant-restore cache so the first
 * paint does not flicker through the default.
 */
import { createContext, useContext, useEffect, useState } from "react";
import { useMutation, useQuery } from "convex/react";
import { Briefcase, Code, User } from "lucide-react";
import { api } from "@convex/_generated/api";

export type WorkMode = "personal" | "work" | "programming";

/**
 * Each mode carries its own colour and icon, carried over from v2. The colour
 * is the point: it is the only signal telling you which mode you are in, so it
 * has to differ at a glance rather than by reading the label.
 */
export const WORK_MODES: {
  value: WorkMode;
  label: string;
  icon: typeof User;
  text: string;
  border: string;
}[] = [
  { value: "personal", label: "Personal", icon: User, text: "text-blue-400", border: "border-blue-400" },
  { value: "work", label: "Work", icon: Briefcase, text: "text-amber-500", border: "border-amber-500" },
  { value: "programming", label: "Coding", icon: Code, text: "text-primary", border: "border-primary" },
];

const STORAGE_KEY = "dashbase-work-mode";

interface WorkModeContextValue {
  workMode: WorkMode;
  setWorkMode: (mode: WorkMode) => void;
}

const WorkModeContext = createContext<WorkModeContextValue>({
  workMode: "personal",
  setWorkMode: () => {},
});

function cached(): WorkMode {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === "personal" || stored === "work" || stored === "programming") {
      return stored;
    }
  } catch {
    // Blocked site data — fall through to the default.
  }
  return "personal";
}

export function WorkModeProvider({ children }: { children: React.ReactNode }) {
  const me = useQuery(api.users.me);
  const persist = useMutation(api.users.setWorkMode);
  const [workMode, setLocal] = useState<WorkMode>(cached);

  // Adopt the server's value once it arrives, but only when it differs from
  // what we already show, so this does not fight the user's own clicks.
  useEffect(() => {
    const remote = me?.lastWorkMode;
    if (
      remote === "personal" ||
      remote === "work" ||
      remote === "programming"
    ) {
      setLocal((current) => (current === remote ? current : remote));
    }
  }, [me?.lastWorkMode]);

  const setWorkMode = (mode: WorkMode) => {
    setLocal(mode);
    try {
      localStorage.setItem(STORAGE_KEY, mode);
    } catch {
      // Caching is a convenience; the server copy is the real one.
    }
    persist({ workMode: mode }).catch((error) =>
      console.warn("Could not save work mode", error),
    );
  };

  return (
    <WorkModeContext.Provider value={{ workMode, setWorkMode }}>
      {children}
    </WorkModeContext.Provider>
  );
}

export const useWorkMode = () => useContext(WorkModeContext);
